import { BASE, SITE_CONFIG } from '../lib/basePath';
/**
 * 0.36.733 - Admin subdomain detection.
 *
 * Each installation runs on two hostnames, one build:
 *   the public site        caf.govtech.am, ilc.govtech.am
 *   the admin panel        gt18-admin.govtech.am, ilc-admin.govtech.am
 *
 * App.jsx branches on `isAdminHost()` to expose admin routes only, hide
 * the citizen landing, and redirect Login.jsx to /admin on success.
 *
 * 0.44.89 - Several installations share the build, so the admin
 * hostname is recognised by rule: its first label ends with "-admin"
 * (gt18-admin, ilc-admin). Hostnames that do not follow the rule can be
 * listed in ADMIN_HOSTNAMES below. The backend has its own list
 * (ADMIN_HOSTNAMES in backend/.env), which must name the same host.
 *
 * NOT a security boundary. All admin routes are already protected
 * by AdminRoute + RoleRoute wrappers that check the server-side
 * session. This hostname split is a UX affordance (dedicated
 * admin URL for bookmarks / muscle memory), not access control.
 */
const ADMIN_HOSTNAMES = new Set([
  'gt18-admin.govtech.am',
]);

/* 0.47.96 - The admin addresses set in Settings > Addresses (an IP
 * address, a subdomain), written into the start page by the server. */
const configuredAdminHosts = () => {
  const list = SITE_CONFIG && Array.isArray(SITE_CONFIG.admin_hosts) ? SITE_CONFIG.admin_hosts : [];
  return new Set(list.map(x => String(x || '').toLowerCase()));
};

export function isAdminHostname(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (ADMIN_HOSTNAMES.has(h)) return true;
  if (configuredAdminHosts().has(h)) return true;
  const first = h.split('.')[0];
  return first.endsWith('-admin') && h.includes('.');
}

export function isAdminHost() {
  if (typeof window === 'undefined') return false;
  // 0.47.96 - Under another site's path it is always the public site.
  if (BASE) return false;
  return isAdminHostname(window.location.hostname);
}

/* 0.44.89 - The admin panel's address, for the "Admin panel" link on
 * the public site. The backend sends it with the public settings
 * (admin_url, from ADMIN_HOSTNAMES); until they arrive, /admin. */
let adminUrl = '';

export function setAdminPanelUrl(url) {
  adminUrl = url || '';
}

export function adminPanelUrl() {
  return adminUrl || '/admin';
}

/* 0.46.68 - The public site's address, for View public site in the
 * admin menu (on the admin hostname "/" is the admin panel itself). The
 * backend sends it with the public settings (public_url); until they
 * arrive, "/". */
let publicUrl = '';

export function setPublicSiteUrl(url) {
  publicUrl = url || '';
}

export function publicSiteUrl() {
  return (isAdminHost() && publicUrl) ? `${publicUrl.replace(/\/$/, '')}/` : '/';
}
